import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pencil, Plus, Star, Trash2 } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { CATEGORIES, STOCK_STATUSES } from "../../lib/constants";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const EMPTY = {
  name: "",
  category: "Country Decor",
  price: "",
  description: "",
  details: "",
  image_url: "",
  stock_status: "In Stock",
  featured: false,
};

const inputCls =
  "w-full rounded-md border border-[#D8CDBC] bg-[#FDFBF7] px-3 py-2 text-sm text-[#292725] placeholder:text-[#8A7E70] focus:border-[#C27803] focus:outline-none focus:ring-2 focus:ring-[#C27803]/30";

export default function ProductsTab({ onChanged }) {
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
  useEffect(() => {
    load();
  }, []);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY);
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      category: p.category,
      price: String(p.price),
      description: p.description,
      details: p.details || "",
      image_url: p.image_url || "",
      stock_status: p.stock_status,
      featured: !!p.featured,
    });
    setOpen(true);
  };

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, price: parseFloat(form.price) || 0 };
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, payload);
        toast.success("Product updated on the shelf");
      } else {
        await api.post("/products", payload);
        toast.success("New product added to the shop");
      }
      setOpen(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Remove "${p.name}" from the shop?`)) return;
    try {
      await api.delete(`/products/${p.id}`);
      toast.success("Product removed");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-[#292725]">{products.length} products</h2>
        <button
          onClick={openNew}
          data-testid="admin-add-product-button"
          className="flex items-center gap-2 rounded-md bg-[#C27803] px-4 py-2.5 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#9A5E02]"
        >
          <Plus className="h-4 w-4" /> Add Product
        </button>
      </div>

      <div className="mt-5 overflow-x-auto rounded-md border border-[#D8CDBC] bg-[#FDFBF7] shadow-sm">
        <table className="w-full min-w-[720px] text-left text-sm">
          <thead>
            <tr className="border-b border-[#D8CDBC] font-mono text-[10px] uppercase tracking-[0.15em] text-[#8A7E70]">
              <th className="px-4 py-3">Product</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Price</th>
              <th className="px-4 py-3">Stock</th>
              <th className="px-4 py-3">Featured</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-dashed divide-[#E8DFC8]">
            {products.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.id}`} className="align-middle">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <img src={p.image_url} alt="" className="h-10 w-12 rounded-sm border border-[#D8CDBC] object-cover" />
                    <span className="font-medium text-[#292725]">{p.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-[#5C554B]">{p.category}</td>
                <td className="px-4 py-3 font-semibold text-[#8C251C]">${p.price.toFixed(2)}</td>
                <td className="px-4 py-3 text-[#5C554B]">{p.stock_status}</td>
                <td className="px-4 py-3">{p.featured ? <Star className="h-4 w-4 fill-[#C27803] text-[#C27803]" /> : <span className="text-[#D8CDBC]">—</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex justify-end gap-2">
                    <button onClick={() => openEdit(p)} data-testid={`admin-edit-product-${p.id}`} className="rounded-md border border-[#D8CDBC] p-2 text-[#5C554B] transition-colors hover:border-[#C27803] hover:text-[#C27803]" aria-label="Edit">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`admin-delete-product-${p.id}`} className="rounded-md border border-[#D8CDBC] p-2 text-[#5C554B] transition-colors hover:border-[#8C251C] hover:text-[#8C251C]" aria-label="Delete">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto border-[#D8CDBC] bg-[#FDFBF7] sm:max-w-xl" data-testid="product-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl text-[#292725]">{editing ? "Edit Product" : "Add a New Product"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Name *</label>
              <input data-testid="product-form-name" required value={form.name} onChange={set("name")} className={inputCls} placeholder="Primitive Crow Shelf Sitter" />
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Category *</label>
                <select data-testid="product-form-category" value={form.category} onChange={set("category")} className={inputCls}>
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Price (USD) *</label>
                <input data-testid="product-form-price" required type="number" step="0.01" min="0" value={form.price} onChange={set("price")} className={inputCls} placeholder="24.00" />
              </div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Stock Status</label>
                <select data-testid="product-form-stock" value={form.stock_status} onChange={set("stock_status")} className={inputCls}>
                  {STOCK_STATUSES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Image URL</label>
                <input data-testid="product-form-image" value={form.image_url} onChange={set("image_url")} className={inputCls} placeholder="https://..." />
              </div>
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Description *</label>
              <textarea data-testid="product-form-description" required rows={3} value={form.description} onChange={set("description")} className={inputCls} />
            </div>
            <div>
              <label className="mb-1 block text-xs font-semibold uppercase tracking-wider text-[#8A7E70]">Details (materials, size, care)</label>
              <textarea data-testid="product-form-details" rows={2} value={form.details} onChange={set("details")} className={inputCls} />
            </div>
            <label className="flex items-center gap-2 text-sm text-[#292725]">
              <input type="checkbox" data-testid="product-form-featured" checked={form.featured} onChange={set("featured")} className="h-4 w-4 accent-[#C27803]" />
              Feature on the home page
            </label>
            <button
              type="submit"
              data-testid="product-form-submit"
              disabled={saving}
              className="w-full rounded-md bg-[#C27803] py-3 text-sm font-semibold text-[#FDFBF7] transition-colors hover:bg-[#9A5E02] disabled:opacity-60"
            >
              {saving ? "Saving..." : editing ? "Save Changes" : "Put It on the Shelf"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
